import { Fragment, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import { AlertCircle, Ban, ChartNoAxesColumn, ChevronDown, ChevronRight, ChevronUp, Info, RotateCw, Search, X } from "lucide-react";
import Panel from "@ui/panel/Panel";
import { GD } from "src/GD";
import { useLocale } from "src/hooks/useLocale";
import { msg } from "src/locales/translate";
import { formatDisplayDate, formatDisplayDateTime, parseServerDate } from "src/utils/dateTime";
import { CardBrandMark } from "../conponents/icons/CardBrandMark";
import { HoverTooltip } from "../conponents/HoverTooltip";
import { AUTH_STATE } from "src/services/AuthService";
import { hasRole, OVERVIEW_ANALYSIS_USER_IDS } from "src/permissions";
import { useTranslation } from "react-i18next";
import { getNotificationErrorMessage } from "src/tools/notify";
import { useBanStatus } from "../bans/useBanStatus";
import { contextBanEntity } from "../bans/contextBanEntity";
import type { BanStatusContext } from "../bans/banStatusCache";
import { BankAnalysisPopup } from "../statView/BankAnalysisPopup";
import type { BankAnalysisRequest } from "../statView/overviewBankAnalysis";
import { BRAND_MARK_HEIGHT, cardBrandOfMask, chargebackCategory, describeChargebackReason, Shimmer } from "./Chargebacks";

type TAmounts = Record<string, number>;

interface IAnalyticsStats {
	chargebacks: number;
	amount: TAmounts;
	transactions?: number;
	ratio_pct?: number;
}

interface IAnalyticsMid extends IAnalyticsStats {
	id: number;
	name?: string;
}

interface IAnalyticsClient extends IAnalyticsStats {
	client_id: number;
	name?: string;
	mids?: IAnalyticsMid[];
}

interface IAnalyticsReason {
	card_type?: string;
	reason_code: string;
	category: string;
	count: number;
	amount: TAmounts;
}

interface IRepeatPayer {
	key: string;
	masked?: string;
	chargebacks: number;
	amount: TAmounts;
	first_seen?: string;
	last_seen?: string;
	clients?: Array<{ id: number; name?: string; chargebacks: number }>;
	linked?: string[];
}

interface IChargebackAnalytics {
	period?: { from: string; to: string };
	totals?: { chargebacks: number; amount: TAmounts; clients?: number };
	by_client?: IAnalyticsClient[];
	by_reason?: IAnalyticsReason[];
	repeat_payers?: { min_count?: number; by_email?: IRepeatPayer[]; by_card?: IRepeatPayer[] };
	unresolved?: number;
}

type TCategoryTone = { color: string; background: string; border: string };

const PERIODS = [30, 90] as const;
type TPeriod = (typeof PERIODS)[number];
type TPayerView = "email" | "card";

// Visa and Mastercard monitoring programmes start around 0.9–1% (backend note on ratio_pct).
const RATIO_ALERT_PCT = 0.9;
const OPEN_KEY = "chargebackAnalytics.open";
const PREVIEW_ROWS = 5;

const readOpen = () => {
	try {
		return localStorage.getItem(OPEN_KEY) === "1";
	} catch {
		return false;
	}
};

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

const periodRequest = (days: TPeriod, clientID?: number) => {
	const to = new Date();
	const from = new Date(to.getTime() - (days - 1) * 86_400_000);
	return { method: "chargebacks/analytics", data: { from: isoDay(from), to: isoDay(to), ...(clientID != null ? { client_id: clientID } : {}) } };
};

const validDate = (value?: string) => {
	const date = value ? parseServerDate(value) : null;
	return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

const formatDay = (value: string) => {
	const date = validDate(value);
	return date ? formatDisplayDate(date) : value;
};

const formatMoment = (value?: string) => {
	const date = validDate(value);
	return date ? formatDisplayDateTime(date, { separator: " " }) : value;
};

const formatRatio = (value: number) => `${value.toFixed(value < 1 ? 3 : 2)}%`;

/** Same look as the chargeback list below: "5958.87" with the currency small and grey. Amounts here are in major units. */
const Amounts = ({ amounts }: { amounts?: TAmounts }) => {
	const entries = Object.entries(amounts ?? {});
	if (!entries.length) return <span className="muted">—</span>;
	return (
		<>
			{entries.map(([currency, value], index) => (
				<Fragment key={currency}>
					{index > 0 && <br />}
					{value.toFixed(2)}
					<small>{currency}</small>
				</Fragment>
			))}
		</>
	);
};

const amountsText = (amounts?: TAmounts) =>
	Object.entries(amounts ?? {})
		.map(([currency, value]) => `${value.toFixed(2)} ${currency}`)
		.join(" · ");

const Root = styled.section`
	min-width: 0;
	overflow: hidden;
	border: 1px solid #d0d5dd;
	border-radius: 12px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
	container-type: inline-size;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;

	/* Header: a native button covers it, the period switch sits above its hit area (as on the dashboard overview). */
	.header { position: relative; display: flex; align-items: center; gap: 16px; min-height: 64px; padding: 0 16px 0 20px; }
	.toggle { position: absolute; inset: 0; z-index: 0; width: 100%; height: 100%; padding: 0; border: 0; background: transparent; cursor: pointer; }
	.toggle:hover { background: #f8fafc; }
	.toggle:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	.heading { position: relative; z-index: 1; pointer-events: none; display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; padding: 12px 0; }
	.heading > svg { flex-shrink: 0; color: #667085; }
	.heading strong { display: block; color: #182230; font-size: 14px; font-weight: 600; line-height: 20px; }
	.description { display: block; overflow: hidden; color: #667085; font-size: 13px; line-height: 20px; text-overflow: ellipsis; white-space: nowrap; }
	.description b { color: #182230; font-weight: 600; }
	.chevron { position: relative; z-index: 1; pointer-events: none; flex-shrink: 0; color: #98a2b3; }
	.over { position: relative; z-index: 1; }

	.segmented { display: inline-flex; padding: 2px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; }
	.segmented button { padding: 3px 10px; border: 0; border-radius: 6px; background: none; color: #667085; font: inherit; font-size: 12px; line-height: 18px; white-space: nowrap; cursor: pointer; }
	.segmented button[aria-pressed="true"] { background: #f2f4f7; color: #182230; font-weight: 600; }
	.segmented button:disabled { cursor: default; }

	/* Body: light area with separate white cards, section titles outside them. */
	.body { display: flex; flex-direction: column; gap: 20px; padding: 20px; border-top: 1px solid #eaecf0; background: #f5f7fa; }
	.block { display: flex; flex-direction: column; gap: 10px; min-width: 0; }
	.block-title { display: flex; align-items: center; gap: 6px; min-height: 26px; color: #667085; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; line-height: 16px; text-transform: uppercase; }
	.block-title .grow { flex: 1; }
	.block-title .segmented { text-transform: none; letter-spacing: 0; font-weight: 400; }
	.card { min-width: 0; overflow: hidden; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
	.hint { display: inline-flex; color: #98a2b3; cursor: help; }
	/* Explanation beside a title: text when it fits, an info icon with a tooltip when it does not. */
	.hint-wrap { display: flex; align-items: center; gap: 6px; flex: 1; min-width: 0; }
	.hint-text { flex: 1; min-width: 0; overflow: hidden; color: #98a2b3; font-size: 12px; font-weight: 400; letter-spacing: 0; line-height: 18px; text-transform: none; text-overflow: clip; white-space: nowrap; }
	.hint-text.hidden { visibility: hidden; }
	.columns { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; align-items: start; }
	.show-more { display: flex; align-items: center; justify-content: center; gap: 6px; width: 100%; min-height: 38px; padding: 8px 16px; border: 0; border-top: 1px solid #eaecf0; background: #fff; color: #175cd3; font: inherit; font-size: 12px; cursor: pointer; }
	.show-more:hover { background: #f8fafc; }
	.show-more:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }

	.kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 12px; }
	.kpi { display: flex; flex-direction: column; gap: 4px; padding: 16px 18px; }
	.kpi-label { display: flex; align-items: center; gap: 4px; color: #667085; font-size: 12px; line-height: 18px; }
	.kpi-value { color: #182230; font-size: 22px; font-weight: 600; line-height: 30px; font-variant-numeric: tabular-nums; white-space: nowrap; }
	.kpi-value small, .num small { margin-left: 4px; color: #98a2b3; font-size: 11px; font-weight: 400; }
	.kpi-note { display: flex; flex-direction: column; gap: 6px; color: #475467; font-size: 13px; line-height: 20px; }
	.kpi.alert .kpi-value { color: #b42318; }
	.kpi.warn .kpi-value { color: #b54708; }

	/* Ratio gauge: fill against the scheme threshold marker. */
	.gauge { position: relative; display: block; width: 100%; height: 6px; margin-top: 6px; border-radius: 3px; background: #f2f4f7; }
	.gauge i { position: absolute; inset: 0 auto 0 0; border-radius: 3px; background: #12b76a; }
	.gauge.alert i { background: #f04438; }
	.gauge b { position: absolute; top: -3px; bottom: -3px; width: 2px; border-radius: 1px; background: #98a2b3; }

	.row { display: grid; align-items: center; gap: 16px; padding: 14px 18px; }
	.row + .row { border-top: 1px solid #eaecf0; }
	.row > * { min-width: 0; }
	.thead { padding-block: 10px; background: #f9fafb; border-bottom: 1px solid #eaecf0; color: #98a2b3; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; line-height: 16px; text-transform: uppercase; }
	.thead + .row { border-top: 0; }
	.right { justify-self: end; text-align: right; white-space: nowrap; }
	.num { color: #182230; font-variant-numeric: tabular-nums; }
	.strong { font-weight: 600; }
	.muted { color: #98a2b3; }
	.secondary { display: block; color: #98a2b3; font-size: 12px; line-height: 18px; font-variant-numeric: tabular-nums; }

	.risk .row { grid-template-columns: minmax(0, 1fr) minmax(120px, 0.9fr) 90px; }
	.risk .row.sub { background: #fcfcfd; padding-block: 10px; }
	.risk .row.sub .name { padding-left: 24px; font-weight: 400; color: #475467; }
	.name { display: flex; align-items: center; gap: 8px; color: #182230; font-weight: 500; }
	.name > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.expand { display: inline-flex; flex-shrink: 0; padding: 0; border: 0; background: none; color: #98a2b3; cursor: pointer; }
	.expand:hover { color: #175cd3; }
	.expand-space { width: 16px; flex-shrink: 0; }
	.ratio-cell { display: flex; flex-direction: column; }
	.ratio-value { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; font-size: 12px; line-height: 18px; }
	.ratio-value b { color: #182230; font-weight: 600; font-variant-numeric: tabular-nums; }
	.ratio-value.alert b { color: #b42318; }

	.mix { display: flex; flex-direction: column; gap: 10px; padding: 16px 18px; border-bottom: 1px solid #eaecf0; }
	.mix-bar { display: flex; height: 8px; overflow: hidden; border-radius: 4px; background: #f2f4f7; }
	.mix-bar i { display: block; height: 100%; }
	.mix-bar i + i { border-left: 2px solid #fff; }
	.legend { display: flex; flex-wrap: wrap; gap: 6px 16px; font-size: 12px; line-height: 18px; color: #475467; }
	.legend span { display: inline-flex; align-items: center; gap: 6px; }
	.legend em { width: 8px; height: 8px; border-radius: 2px; }
	.legend b { color: #182230; font-weight: 600; font-variant-numeric: tabular-nums; }
	.reason-line { display: flex; align-items: center; gap: 8px; min-width: 0; }
	.reason-item + .reason-item { border-top: 1px solid #eaecf0; }
	.reason-item.expandable > .row { cursor: pointer; }
	.reason-item.expandable > .row:hover { background: #f8fafc; }
	.reason-item.open > .row { background: #f8fafc; }
	.reason-item > .row:focus-visible { outline: 2px solid #6374d9; outline-offset: -2px; }
	.reasons .card { container-type: inline-size; }
	.reasons .row { grid-template-columns: minmax(0, 1fr) 90px 100px 16px; }
	.reason-item { position: relative; }
	.reason-item.open::before { content: ""; position: absolute; inset: 0 auto 0 0; width: 3px; background: var(--tone); }
	.reason-chevron { display: inline-flex; justify-self: end; color: #98a2b3; }
	.reason-item.expandable > .row:hover .reason-chevron { color: #175cd3; }
	/* Indented to the reason code; two short columns read easier than one line across the card. */
	.reason-details { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 32px; padding: 0 18px 16px 60px; background: #f8fafc; }
	.detail { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
	.detail-label { color: #667085; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; line-height: 16px; text-transform: uppercase; }
	.detail p { max-width: 62ch; margin: 0; color: #344054; font-size: 13px; line-height: 20px; }
	.source { grid-column: 1 / -1; color: #98a2b3; font-size: 11px; line-height: 16px; }
	@container (max-width: 560px) {
		.reason-details { grid-template-columns: minmax(0, 1fr); padding-left: 18px; }
	}
	.brand-slot { display: inline-flex; align-items: center; justify-content: center; flex: 0 0 34px; width: 34px; }
	.code { flex: 0 0 auto; min-width: 42px; }
	.reason-line b { color: #182230; font-weight: 600; font-variant-numeric: tabular-nums; }
	.reason-line span { overflow: hidden; color: #344054; text-overflow: ellipsis; white-space: nowrap; }
	.share { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
	.share-bar { width: 72px; height: 4px; overflow: hidden; border-radius: 2px; background: #f2f4f7; }
	.share-bar i { display: block; height: 100%; border-radius: 2px; }

	.payers .row { grid-template-columns: minmax(180px, 1.2fr) minmax(160px, 1fr) minmax(170px, 1fr) 90px 110px; }
	.payer-key { display: flex; align-items: center; gap: 8px; min-width: 0; color: #182230; font-weight: 500; }
	.payer-key > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
	.payer-ban { display: inline-flex; align-items: center; gap: 4px; width: max-content; max-width: 100%; margin-top: 4px; padding: 2px 6px; border: 1px solid #eaecf0; border-radius: 6px; color: #667085; background: #f9fafb; font-size: 11px; font-weight: 500; line-height: 16px; }
	.payer-ban[data-banned="true"] { border-color: #fecdca; color: #b42318; background: #fef3f2; }
	.payer-ban svg { flex-shrink: 0; }
	.linked-value { display: inline-flex; flex-direction: column; align-items: flex-start; max-width: 100%; }
	.chips { display: flex; flex-wrap: wrap; gap: 6px; }
	.chip { display: inline-flex; align-items: center; gap: 6px; max-width: 100%; padding: 1px 6px; border: 1px solid #eaecf0; border-radius: 6px; background: #f9fafb; color: #475467; font-size: 12px; line-height: 18px; font-variant-numeric: tabular-nums; }
	.chip > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	button.chip { font: inherit; font-size: 12px; cursor: pointer; }
	button.chip:hover, button.link:hover { border-color: #b2ddff; background: #eff8ff; color: #175cd3; }
	.chip.more { color: #344054; font-weight: 600; }
	button.link { display: inline-flex; max-width: 100%; margin: -2px -6px; padding: 2px 6px; border: 1px solid transparent; border-radius: 6px; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
	.dates { font-size: 12px; line-height: 18px; color: #475467; font-variant-numeric: tabular-nums; }
	.count { display: inline-flex; padding: 1px 8px; border: 1px solid #fecdca; border-radius: 6px; background: #fef3f2; color: #b42318; font-size: 12px; font-weight: 600; line-height: 18px; font-variant-numeric: tabular-nums; }

	.empty { padding: 20px 18px; color: #98a2b3; }
	.error { display: flex; align-items: center; gap: 8px; padding: 16px 18px; color: #b42318; }
	.retry { display: inline-flex; align-items: center; gap: 4px; margin-left: auto; padding: 4px 8px; border: 1px solid #d0d5dd; border-radius: 6px; background: #fff; color: #344054; font: inherit; font-size: 12px; cursor: pointer; }
	.cell-label { display: none; }

	@container (max-width: 1100px) {
		.columns { grid-template-columns: minmax(0, 1fr); }
	}
	@container (max-width: 760px) {
		.body { padding: 16px; }
		.payers .thead { display: none; }
		.payers .row { grid-template-columns: minmax(0, 1fr) auto; gap: 6px 12px; }
		.payers .row > .wide { grid-column: 1 / -1; }
		.payers .row > .count-cell { grid-row: 1; grid-column: 2; }
		.payers .row > .amount-cell { grid-column: 2; justify-self: end; }
		.risk .row { grid-template-columns: minmax(0, 1fr) 100px; }
		.risk .row > .ratio-cell { grid-column: 1 / -1; grid-row: 2; }
	}
`;

/** Explanation next to a title when it fits on one line; otherwise an info icon showing it in a tooltip. */
const Hint = ({ text }: { text: string }) => {
	const ref = useRef<HTMLSpanElement>(null);
	const [fits, setFits] = useState(true);
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const check = () => setFits(element.scrollWidth <= element.clientWidth + 1);
		check();
		const observer = new ResizeObserver(check);
		observer.observe(element);
		return () => observer.disconnect();
	}, [text]);
	return (
		<span className="hint-wrap">
			{!fits && (
				<HoverTooltip
					content={<span style={{ display: "block", maxWidth: "320px", color: "#fff", fontSize: "12px", lineHeight: "18px", whiteSpace: "normal", textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>{text}</span>}
					style={{ display: "inline-flex" }}>
					<span className="hint" aria-label={text}>
						<Info size={12} />
					</span>
				</HoverTooltip>
			)}
			<span ref={ref} className={`hint-text${fits ? "" : " hidden"}`} aria-hidden={!fits}>
				{text}
			</span>
		</span>
	);
};

/** Gauge scaled so the scheme threshold always sits on the track and a high ratio still fits. */
const RatioGauge = ({ value }: { value: number }) => {
	const max = Math.max(RATIO_ALERT_PCT * 1.5, value * 1.1);
	return (
		<span className={`gauge${value >= RATIO_ALERT_PCT ? " alert" : ""}`}>
			<i style={{ width: `${Math.max((value / max) * 100, value > 0 ? 1.5 : 0)}%` }} />
			<b style={{ left: `${(RATIO_ALERT_PCT / max) * 100}%` }} />
		</span>
	);
};

const RatioCell = ({ value }: { value?: number }) =>
	value == null ? (
		<span className="ratio-cell muted">—</span>
	) : (
		<span className="ratio-cell">
			<span className={`ratio-value${value >= RATIO_ALERT_PCT ? " alert" : ""}`}>
				<b>{formatRatio(value)}</b>
				<span className="muted">
					{msg("cbaThreshold")} {formatRatio(RATIO_ALERT_PCT)}
				</span>
			</span>
			<RatioGauge value={value} />
		</span>
	);

const StatsCells = ({ stats }: { stats: IAnalyticsStats }) => (
	<>
		<RatioCell value={stats.ratio_pct} />
		<span className="right">
			<span className="num strong">
				<Amounts amounts={stats.amount} />
			</span>
			<span className="secondary">
				{stats.chargebacks}
				{stats.transactions != null && ` / ${stats.transactions}`}
			</span>
		</span>
	</>
);

const RiskRows = ({ client }: { client: IAnalyticsClient }) => {
	useLocale();
	const [open, setOpen] = useState(false);
	const mids = client.mids ?? [];
	return (
		<>
			<div className="row">
				<span className="name">
					{mids.length > 1 ? (
						<button type="button" className="expand" aria-expanded={open} title={msg("cbaShowMids")} onClick={() => setOpen(!open)}>
							{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
						</button>
					) : (
						<i className="expand-space" />
					)}
					<span title={client.name}>{client.name || client.client_id}</span>
				</span>
				<StatsCells stats={client} />
			</div>
			{open &&
				mids.map(mid => (
					<div key={mid.id} className="row sub">
						<span className="name">
							<span title={mid.name}>{mid.name || mid.id}</span>
						</span>
						<StatsCells stats={mid} />
					</div>
				))}
		</>
	);
};

/** Who is at risk, highest ratio first. Rows come from riskRows(). */
const RiskBlock = ({ rows, merchants, owner }: { rows: IAnalyticsClient[]; merchants: boolean; owner?: string }) => {
	useLocale();
	const [expanded, setExpanded] = useState(false);
	return (
		<div className="block risk">
			<div className="block-title">
				{merchants ? msg("cbaByMid") : msg("cbaByClient")}
				{owner && <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>· {owner}</span>}
				<Hint text={msg("cbaRatioHint")} />
			</div>
			<div className="card">
				{rows.length ? (
					<>
						<div className="row thead">
							<span>{merchants ? msg("merchant") : msg("client")}</span>
							<span>{msg("cbaRatio")}</span>
							<span className="right" title={msg("cbaCountHint")}>
								{msg("amount")}
							</span>
						</div>
						{(expanded ? rows : rows.slice(0, PREVIEW_ROWS)).map(client => (
							<RiskRows key={client.client_id} client={client} />
						))}
						{rows.length > PREVIEW_ROWS && <ShowMore expanded={expanded} total={rows.length} onClick={() => setExpanded(value => !value)} />}
					</>
				) : (
					<div className="empty">{msg("cbaEmpty")}</div>
				)}
			</div>
		</div>
	);
};

type TReasonInfo = ReturnType<typeof describeChargebackReason>;

/** One reason code; when the code is known, a click opens the same explanation as the chargeback popup. */
const ReasonRow = ({ item, reason, brand, category, share }: { item: IAnalyticsReason; reason: TReasonInfo["reason"]; brand: TReasonInfo["brand"]; category: { label: string; tone: TCategoryTone }; share: number }) => {
	useLocale();
	const [open, setOpen] = useState(false);
	return (
		<div className={`reason-item${reason ? " expandable" : ""}${open ? " open" : ""}`} style={{ ["--tone" as string]: category.tone.color }}>
			<div
				className="row"
				role={reason ? "button" : undefined}
				tabIndex={reason ? 0 : undefined}
				aria-expanded={reason ? open : undefined}
				onClick={reason ? () => setOpen(!open) : undefined}
				onKeyDown={reason ? event => (event.key === "Enter" || event.key === " ") && (event.preventDefault(), setOpen(!open)) : undefined}>
				<span className="reason-line" title={category.label}>
					<span className="brand-slot">{brand && <CardBrandMark brand={brand} height={BRAND_MARK_HEIGHT[brand].row} />}</span>
					<b className="code">{item.reason_code}</b>
					<span title={reason?.title()}>{reason ? reason.title() : category.label}</span>
				</span>
				<span className="share" title={`${Math.round(share)}%`}>
					<span className="num strong">{item.count}</span>
					<span className="share-bar">
						<i style={{ width: `${share}%`, backgroundColor: category.tone.color }} />
					</span>
				</span>
				<span className="right num">
					<Amounts amounts={item.amount} />
				</span>
				<span className="reason-chevron">{reason && (open ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}</span>
			</div>
			{open && reason && (
				<div className="reason-details">
					<div className="detail">
						<span className="detail-label">{msg("cbReasonExplainer")}</span>
						<p>{reason.about()}</p>
					</div>
					<div className="detail">
						<span className="detail-label">{msg("cbReasonEvidence")}</span>
						<p>{reason.evidence()}</p>
					</div>
					<span className="source">{reason.source()}</span>
				</div>
			)}
		</div>
	);
};

/** Why: the mix of dispute groups, then each scheme reason code. */
const ReasonsBlock = ({ reasons }: { reasons: IAnalyticsReason[] }) => {
	useLocale();
	const [expanded, setExpanded] = useState(false);
	const total = reasons.reduce((sum, item) => sum + item.count, 0) || 1;
	const rows = reasons.map(item => {
		const { reason, brand } = describeChargebackReason(item.reason_code, item.card_type);
		const category = reason ? { label: reason.category.label(), tone: reason.category.tone as TCategoryTone } : chargebackCategory(item.category);
		return { item, reason, brand, category, share: (item.count / total) * 100 };
	});
	const mix = new Map<string, { label: string; tone: TCategoryTone; count: number }>();
	rows.forEach(({ item, category }) => {
		const entry = mix.get(category.label) ?? { label: category.label, tone: category.tone, count: 0 };
		entry.count += item.count;
		mix.set(category.label, entry);
	});

	return (
		<div className="block reasons">
			<div className="block-title">{msg("cbaByReason")}</div>
			<div className="card">
				{rows.length ? (
					<>
						<div className="mix">
							<span className="mix-bar">
								{[...mix.values()].map(group => (
									<i key={group.label} title={group.label} style={{ width: `${(group.count / total) * 100}%`, backgroundColor: group.tone.color }} />
								))}
							</span>
							<span className="legend">
								{[...mix.values()].map(group => (
									<span key={group.label}>
										<em style={{ backgroundColor: group.tone.color }} />
										{group.label} <b>{Math.round((group.count / total) * 100)}%</b>
									</span>
								))}
							</span>
						</div>
						{(expanded ? rows : rows.slice(0, PREVIEW_ROWS)).map(({ item, reason, brand, category, share }) => (
							<ReasonRow key={`${item.card_type}-${item.reason_code}`} item={item} reason={reason} brand={brand} category={category} share={share} />
						))}
						{rows.length > PREVIEW_ROWS && <ShowMore expanded={expanded} total={rows.length} onClick={() => setExpanded(value => !value)} />}
					</>
				) : (
					<div className="empty">{msg("cbaEmpty")}</div>
				)}
			</div>
		</div>
	);
};

const LINKED_INLINE = 2;
const LINKED_SEARCH_FROM = 12;

const isCardMask = (value: string) => /^\d/.test(value);

/** Email analysis opens from any email shown here, when the user may analyse (as in the transaction card). */
type TAnalyse = ((email: string) => void) | undefined;

const ShowMore = ({ expanded, total, onClick }: { expanded: boolean; total: number; onClick: () => void }) => (
	<button type="button" className="show-more" aria-expanded={expanded} onClick={onClick}>
		{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
		{expanded ? msg("showLess") : msg("showMore")}{!expanded && ` · ${total - PREVIEW_ROWS}`}
	</button>
);

/** An exact email can be checked against banlist; a card mask cannot identify a card token. */
const PayerEmailStatus = ({ email, context }: { email: string; context?: BanStatusContext }) => {
	const { t } = useTranslation();
	const entity = contextBanEntity("email", email);
	const status = useBanStatus(context && entity ? { ...context, ...entity } : null);
	if (!context || !entity) return null;
	const rows = status.snapshot?.rows ?? [];
	const label = rows.length ? t("bans.status.banned", { count: rows.length }) : t(status.busy ? "bans.status.checking" : status.error ? "bans.status.unknown" : "bans.status.none");
	const details = rows.map(row => [t(`bans.targets.${String(row.target_type)}`, { defaultValue: String(row.target_type) }), `#${String(row.target_id)}`, row.reason, row.display_reason].filter(Boolean).join(" · ")).join("\n");
	return <span className="payer-ban" data-banned={rows.length > 0 || undefined} role="status" title={`${email}\n${details || label}`}>
		{rows.length > 0 && <Ban size={12} aria-hidden="true" />}{label}
	</span>;
};

const LinkedChip = ({ value, onAnalyse }: { value: string; onAnalyse: TAnalyse }) => {
	const brand = isCardMask(value) ? cardBrandOfMask(value) : undefined;
	const content = (
		<>
			{brand && <CardBrandMark brand={brand} height={brand === "visa" ? 7 : 10} />}
			<span>{value}</span>
		</>
	);
	return onAnalyse && !isCardMask(value) ? (
		<button type="button" className="chip" title={msg("cbaAnalyseEmail", { v0: value })} onClick={() => onAnalyse(value)}>
			{content}
		</button>
	) : (
		<span className="chip" title={value}>
			{content}
		</span>
	);
};

/** First values inline; the rest behind a "+N" chip that opens the full list. */
const LinkedValues = ({ values, onShowAll, onAnalyse, banContext }: { values?: string[]; onShowAll: () => void; onAnalyse: TAnalyse; banContext?: BanStatusContext }) => {
	if (!values?.length) return <span className="muted">—</span>;
	const hidden = values.length - LINKED_INLINE;
	return (
		<>
			{values.slice(0, LINKED_INLINE).map(value => (
				<span className="linked-value" key={value}>
					<LinkedChip value={value} onAnalyse={onAnalyse} />
					<PayerEmailStatus email={value} context={banContext} />
				</span>
			))}
			{hidden > 0 && (
				<button type="button" className="chip more" title={msg("cbaShowAllLinked", { v0: values.length })} onClick={onShowAll}>
					+{hidden}
				</button>
			)}
		</>
	);
};

const ListPopup = styled.div`
	display: flex;
	flex-direction: column;
	min-height: 0;
	flex: 1;
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;
	.top { display: flex; align-items: flex-start; gap: 12px; padding: 20px 20px 16px 24px; border-bottom: 1px solid #edf0f5; background: #fff; }
	.top > div { flex: 1; min-width: 0; }
	.context { color: #667085; font-size: 11px; line-height: 16px; }
	.title { overflow: hidden; color: #182230; font-size: 21px; font-weight: 600; line-height: 28px; text-overflow: ellipsis; white-space: nowrap; }
	.close { display: flex; align-items: center; justify-content: center; flex-shrink: 0; width: 36px; height: 36px; padding: 0; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #344054; cursor: pointer; }
	.close:hover { background: #f2f4f7; color: #182230; }
	.close:focus-visible { outline: 2px solid #6374d9; outline-offset: 2px; }
	.content { display: flex; flex-direction: column; gap: 10px; flex: 1; min-height: 0; overflow-y: auto; padding: 20px 24px 24px; background: #f5f7fa; }
	.label { color: #667085; font-size: 11px; font-weight: 600; letter-spacing: 0.04em; line-height: 16px; text-transform: uppercase; }
	.search { display: flex; align-items: center; gap: 8px; height: 36px; padding: 0 12px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; color: #98a2b3; }
	.search input { flex: 1; min-width: 0; border: 0; outline: 0; background: none; color: #182230; font: inherit; }
	.list { overflow: hidden; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
	.item { display: flex; align-items: center; gap: 10px; min-height: 40px; padding: 8px 18px; font-variant-numeric: tabular-nums; }
	.item + .item { border-top: 1px solid #eaecf0; }
	.item > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
	.item > .linked-identity { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; white-space: normal; overflow-wrap: anywhere; }
	button.item { width: 100%; border: 0; border-top: 1px solid #eaecf0; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
	.list > button.item:first-child { border-top: 0; }
	button.item:hover { background: #f8fafc; color: #175cd3; }
	.empty { padding: 18px; color: #98a2b3; }
`;

/** Every card or email linked to one repeat payer; searchable when the list is long. */
const LinkedListPopup = ({ payer, values, cards, onAnalyse, onClose, banContext }: { payer: string; values: string[]; cards: boolean; onAnalyse: TAnalyse; onClose: () => void; banContext?: BanStatusContext }) => {
	useLocale();
	const [query, setQuery] = useState("");
	const needle = query.trim().toLowerCase();
	const visible = needle ? values.filter(value => value.toLowerCase().includes(needle)) : values;
	return (
		<Panel growContent disableScroll decorator="absolute" onClose={onClose} sx={{ padding: "0", width: "520px", maxWidth: "94vw", maxHeight: "80dvh", borderRadius: "12px", overflow: "hidden", backgroundColor: "#fff" }}>
			<ListPopup>
				<div className="top">
					<div>
						<div className="context">{msg("cbaRepeat")}</div>
						<div className="title" title={payer}>
							{payer}
						</div>
					</div>
					<button type="button" className="close" title={msg("cbClose")} aria-label={msg("cbClose")} onClick={onClose}>
						<X size={18} />
					</button>
				</div>
				<div className="content">
					<div className="label">
						{cards ? msg("cbaLinkedCards") : msg("cbaLinkedEmails")} · {values.length}
					</div>
					{values.length >= LINKED_SEARCH_FROM && (
						<label className="search">
							<Search size={15} />
							<input value={query} placeholder={msg("cbaSearch")} onChange={event => setQuery(event.target.value)} />
						</label>
					)}
					<div className="list">
						{visible.length ? (
							visible.map(value => {
								const brand = isCardMask(value) ? cardBrandOfMask(value) : undefined;
								const inner = (
									<>
										<span className="brand-slot" style={{ display: "inline-flex", justifyContent: "center", flex: "0 0 34px" }}>
											{brand && <CardBrandMark brand={brand} height={BRAND_MARK_HEIGHT[brand].row} />}
										</span>
										<span className="linked-identity">{value}<PayerEmailStatus email={value} context={banContext} /></span>
									</>
								);
								return onAnalyse && !isCardMask(value) ? (
									<button key={value} type="button" className="item" title={msg("cbaAnalyseEmail", { v0: value })} onClick={() => onAnalyse(value)}>
										{inner}
									</button>
								) : (
									<div key={value} className="item">
										{inner}
									</div>
								);
							})
						) : (
							<div className="empty">{msg("cbaNothingFound")}</div>
						)}
					</div>
				</div>
			</ListPopup>
		</Panel>
	);
};

/** Who does it again: payers with several chargebacks, by email or by card. */
const RepeatPayersBlock = ({ data, clientScoped, onAnalyse, banContext }: { data: IChargebackAnalytics["repeat_payers"]; clientScoped: boolean; onAnalyse: TAnalyse; banContext?: BanStatusContext }) => {
	useLocale();
	const [allLinked, setAllLinked] = useState<{ payer: string; values: string[]; cards: boolean } | undefined>(undefined);
	const byEmail = data?.by_email ?? [];
	const byCard = data?.by_card ?? [];
	const [view, setView] = useState<TPayerView>(byEmail.length || !byCard.length ? "email" : "card");
	const [expanded, setExpanded] = useState(false);
	const payers = view === "email" ? byEmail : byCard;
	return (
		<div className="block payers">
			<div className="block-title">
				{msg("cbaRepeat")}
				<Hint text={msg("cbaRepeatHint", { v0: data?.min_count ?? 2 })} />
				{(byEmail.length > 0 || byCard.length > 0) && (
					<span className="segmented" role="group">
						<button type="button" aria-pressed={view === "email"} onClick={() => setView("email")}>
							{msg("cbaByEmail")} · {byEmail.length}
						</button>
						<button type="button" aria-pressed={view === "card"} onClick={() => setView("card")}>
							{msg("cbaByCard")} · {byCard.length}
						</button>
					</span>
				)}
			</div>
			<div className="card">
				{payers.length ? (
					<>
						<div className="row thead">
							<span>{view === "email" ? msg("cbaPayerEmail") : msg("cbaPayerCard")}</span>
							<span>{view === "email" ? msg("cbaLinkedCards") : msg("cbaLinkedEmails")}</span>
							<span>{msg("cbaSeen")}</span>
							<span className="right">{msg("cbaChargebacks")}</span>
							<span className="right">{msg("amount")}</span>
						</div>
						{(expanded ? payers : payers.slice(0, PREVIEW_ROWS)).map(payer => {
							const brand = view === "card" && payer.masked ? cardBrandOfMask(payer.masked) : undefined;
							const clients = clientScoped ? [] : (payer.clients ?? []).map(client => client.name || String(client.id));
							const first = formatMoment(payer.first_seen);
							const last = formatMoment(payer.last_seen);
							return (
								<div key={payer.key} className="row">
									<span className="wide">
										<span className="payer-key">
											{brand && <CardBrandMark brand={brand} height={BRAND_MARK_HEIGHT[brand].row} />}
											{view === "email" && onAnalyse ? (
												<button type="button" className="link" title={msg("cbaAnalyseEmail", { v0: payer.key })} onClick={() => onAnalyse(payer.key)}>
													<span>{payer.key}</span>
												</button>
											) : (
												<span title={payer.key}>{view === "card" ? payer.masked || payer.key : payer.key}</span>
											)}
										</span>
										{view === "email" && <PayerEmailStatus email={payer.key} context={banContext} />}
										{clients.length > 0 && <span className="secondary">{clients.join(", ")}</span>}
									</span>
									<span className="wide chips">
											<LinkedValues
												values={payer.linked}
											banContext={banContext}
												onAnalyse={onAnalyse}
												onShowAll={() => setAllLinked({ payer: view === "card" ? payer.masked || payer.key : payer.key, values: payer.linked ?? [], cards: view === "email" })}
											/>
										</span>
									<span className="wide dates">
										{first}
										{last && last !== first && <span className="secondary">{last}</span>}
									</span>
									<span className="right count-cell">
										<span className="count">{payer.chargebacks}</span>
									</span>
									<span className="right num strong amount-cell">
										<Amounts amounts={payer.amount} />
									</span>
								</div>
							);
						})}
						{payers.length > PREVIEW_ROWS && <ShowMore expanded={expanded} total={payers.length} onClick={() => setExpanded(value => !value)} />}
					</>
				) : (
					<div className="empty">{msg("cbaNoRepeat")}</div>
				)}
			</div>
			{allLinked && <LinkedListPopup payer={allLinked.payer} values={allLinked.values} cards={allLinked.cards} onAnalyse={onAnalyse} banContext={banContext} onClose={() => setAllLinked(undefined)} />}
		</div>
	);
};

const Kpi = ({ label, hint, tone, note, children }: { label: string; hint?: string; tone?: "alert" | "warn"; note?: ReactNode; children: ReactNode }) => (
	<div className={`card kpi${tone ? ` ${tone}` : ""}`}>
		<span className="kpi-label">
			{label}
			{hint && <Hint text={hint} />}
		</span>
		<span className="kpi-value">{children}</span>
		{note && <span className="kpi-note">{note}</span>}
	</div>
);

/**
 * Rows for the risk block. A client page lists the client's merchants; the dashboard lists clients, or the merchants
 * of the only client. With one row left the block would repeat the totals, so the caller hides it.
 */
const riskRows = (clients: IAnalyticsClient[], clientScoped: boolean) => {
	const merchants = clientScoped || clients.length === 1;
	const rows: IAnalyticsClient[] = merchants ? (clients[0]?.mids ?? []).map(mid => ({ ...mid, client_id: mid.id })) : clients;
	return {
		merchants,
		owner: !clientScoped && clients.length === 1 ? clients[0].name : undefined,
		rows: rows.slice().sort((a, b) => (b.ratio_pct ?? -1) - (a.ratio_pct ?? -1)),
	};
};

/** Ratio across the clients in the response: the client's own one when scoped, else chargebacks / transactions summed. */
const overallRatio = (clients: IAnalyticsClient[], clientScoped: boolean) => {
	if (clientScoped) return clients[0]?.ratio_pct;
	const transactions = clients.reduce((sum, client) => sum + (client.transactions ?? 0), 0);
	if (!transactions) return undefined;
	return (clients.reduce((sum, client) => sum + client.chargebacks, 0) / transactions) * 100;
};

const Skeleton = () => (
	<div className="body">
		<div className="kpis">
			{[0, 1, 2, 3].map(index => (
				<div key={index} className="card kpi">
					<Shimmer $width="80px" $height="10px" />
					<Shimmer $width="110px" $height="22px" />
				</div>
			))}
		</div>
		<div className="columns">
			{[0, 1].map(column => (
				<div key={column} className="block">
					<Shimmer $width="110px" $height="10px" />
					<div className="card">
						{[0, 1, 2].map(index => (
							<div key={index} className="row" style={{ gridTemplateColumns: "minmax(0, 1fr) 80px 90px" }}>
								<Shimmer $width={`${50 + index * 15}%`} $height="12px" />
								<Shimmer $width="60px" $height="12px" />
								<Shimmer $width="80px" $height="12px" />
							</div>
						))}
					</div>
				</div>
			))}
		</div>
	</div>
);

/**
 * chargebacks/analytics as a collapsible panel over the chargeback list. It answers, in order: how bad overall, who is
 * at risk, why, and who charges back repeatedly. Data loads on first expand. Without clientID it covers all clients.
 */
export const ChargebackAnalytics = ({ clientID }: { clientID?: number }) => {
	useLocale();
	const titleId = useId();
	const [open, setOpen] = useState(readOpen);
	const [days, setDays] = useState<TPeriod>(90);
	const clientScoped = clientID != null;
	const [project] = GD.B_SELECTED_PROJECT.use();
	const projectId = project?.id;
	const authState = GD.S_AUTH_STATUS.use([]);
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [projectId, authState]);
	const userId = user?.id;
	const [attempt, setAttempt] = useState(0);
	const requestKey = JSON.stringify([clientID, days, projectId, userId, authState, attempt]);
	const [loaded, setLoaded] = useState<{ key: string; response?: IAPIResponse<IChargebackAnalytics>; busy: boolean }>({ key: "", busy: false });
	const response = loaded.key === requestKey ? loaded.response : undefined;
	const busy = open && (loaded.key !== requestKey || loaded.busy);
	const data = response?.data;
	useEffect(() => {
		if (!open || projectId === undefined || userId === undefined || authState !== AUTH_STATE.AUTHORIZED) return;
		let active = true;
		const controller = new AbortController();
		const key = JSON.stringify([clientID, days, projectId, userId, authState, attempt]);
		setLoaded({ key, busy: true });
		const load = async () => {
			try {
				const result = await GD.REQ_API.request({ ...periodRequest(days, clientID), expectedProjectId: projectId, signal: controller.signal });
				if (active) setLoaded({ key, response: result, busy: false });
			} catch (error) {
				if (active) setLoaded({ key, response: { error: { code: -1, message: getNotificationErrorMessage(error, msg("cbaLoadFailed")) } }, busy: false });
			}
		};
		void load();
		return () => { active = false; controller.abort(); };
	}, [open, clientID, days, projectId, userId, authState, attempt]);
	const banContext = authState === AUTH_STATE.AUTHORIZED && user && project && hasRole(user.projects.find(item => item.id === project.id)?.roles, "admin") ? { userId: user.id, projectId: project.id } : undefined;
	const [analysis, setAnalysis] = useState<BankAnalysisRequest | undefined>(undefined);
	useEffect(() => { setAnalysis(undefined); }, [clientID, days, projectId, userId, authState]);
	const canAnalyse = authState === AUTH_STATE.AUTHORIZED && !!user && !!project && OVERVIEW_ANALYSIS_USER_IDS.includes(user.id);
	const analyseEmail: TAnalyse =
		canAnalyse && user && project && data?.period
			? email => setAnalysis({ bank: { type: "email", value: email, label: email }, from: data.period!.from, to: data.period!.to, currency: "", userId: user.id, projectId: project.id })
			: undefined;

	const toggle = () => {
		const next = !open;
		setOpen(next);
		try {
			localStorage.setItem(OPEN_KEY, next ? "1" : "0");
		} catch {
			// Private mode: the panel just forgets its state.
		}
	};

	const selectPeriod = (next: TPeriod) => {
		setDays(next);
	};

	const totals = data?.totals;
	const clients = data?.by_client ?? [];
	const ratio = overallRatio(clients, clientScoped);
	const risk = riskRows(clients, clientScoped);
	const single = risk.rows.length <= 1;
	const transactions = clients.some(client => client.transactions != null) ? clients.reduce((sum, client) => sum + (client.transactions ?? 0), 0) : undefined;
	// Dashboard: how many clients, or the name of the only one (its row is not shown then).
	const clientsNote = clientScoped ? undefined : clients.length === 1 ? clients[0].name : totals?.clients != null ? `${msg("cbaClients")}: ${totals.clients}` : undefined;
	const repeatCount = Math.max(data?.repeat_payers?.by_email?.length ?? 0, data?.repeat_payers?.by_card?.length ?? 0);

	let description: ReactNode = msg("cbaDescription");
	if (data && !response?.error && totals) {
		description = (
			<>
				{msg("cbaChargebacks")}: <b>{totals.chargebacks}</b> · <b>{amountsText(totals.amount) || "0.00"}</b>
				{data.period && ` · ${formatDay(data.period.from)} — ${formatDay(data.period.to)}`}
			</>
		);
	}

	return (
		<Root aria-labelledby={titleId} aria-busy={busy}>
			<header className="header">
				<button type="button" className="toggle" aria-expanded={open} aria-controls={`${titleId}-body`} title={open ? msg("cbaCollapse") : msg("cbaExpand")} onClick={toggle} />
				<span className="heading">
					<ChartNoAxesColumn size={20} aria-hidden="true" />
					<span style={{ minWidth: 0 }}>
						<strong id={titleId}>{msg("cbaTitle")}</strong>
						<span className="description">{description}</span>
					</span>
				</span>
				{open && (
					<div className="segmented over" role="group" aria-label={msg("cbaPeriod")}>
						{PERIODS.map(period => (
							<button key={period} type="button" aria-pressed={days === period} disabled={busy} onClick={() => selectPeriod(period)}>
								{period === 30 ? msg("cbaDays30") : msg("cbaDays90")}
							</button>
						))}
					</div>
				)}
				{open ? <ChevronUp className="chevron" size={17} aria-hidden="true" /> : <ChevronDown className="chevron" size={17} aria-hidden="true" />}
			</header>

			<div id={`${titleId}-body`} hidden={!open}>
				{open &&
					(busy || !response ? (
						<Skeleton />
					) : response.error ? (
						<div className="body">
							<div className="card error">
								<AlertCircle size={18} />
								{response.error.message || msg("cbaLoadFailed")}
								<button type="button" className="retry" onClick={() => setAttempt(value => value + 1)}>
									<RotateCw size={13} />
									{msg("cbaRetry")}
								</button>
							</div>
						</div>
					) : (
						<div className="body">
							<div className="kpis">
								<Kpi label={msg("cbaRatio")} hint={msg("cbaRatioHint")} tone={ratio != null && ratio >= RATIO_ALERT_PCT ? "alert" : undefined} note={
										ratio != null && (
											<>
												<RatioGauge value={ratio} />
												<span>
													{msg("cbaThreshold")} {formatRatio(RATIO_ALERT_PCT)}
												</span>
											</>
										)
									}>
									{ratio != null ? formatRatio(ratio) : "—"}
								</Kpi>
								<Kpi
									label={msg("cbaChargebacks")}
									note={[transactions != null ? msg("cbaTransactionsNote", { v0: transactions }) : undefined, clientsNote].filter(Boolean).join(" · ") || undefined}>
									{totals?.chargebacks ?? 0}
								</Kpi>
								<Kpi label={msg("amount")}>
									<Amounts amounts={totals?.amount} />
								</Kpi>
								<Kpi label={msg("cbaRepeat")} note={msg("cbaRepeatHint", { v0: data?.repeat_payers?.min_count ?? 2 })} tone={repeatCount > 0 ? "warn" : undefined}>
									{repeatCount}
								</Kpi>
								{!!data?.unresolved && (
									<Kpi label={msg("cbaUnresolved")} hint={msg("cbaUnresolvedHint")} tone="warn">
										{data.unresolved}
									</Kpi>
								)}
							</div>
							{single ? (
								<ReasonsBlock reasons={data?.by_reason ?? []} />
							) : (
								<div className="columns">
									<RiskBlock key={requestKey} rows={risk.rows} merchants={risk.merchants} owner={risk.owner} />
									<ReasonsBlock key={requestKey} reasons={data?.by_reason ?? []} />
								</div>
							)}
							<RepeatPayersBlock key={requestKey} data={data?.repeat_payers} clientScoped={clientScoped} onAnalyse={analyseEmail} banContext={banContext} />
						</div>
					))}
			</div>
			{analysis && <BankAnalysisPopup request={analysis} onClose={() => setAnalysis(undefined)} onEntity={bank => setAnalysis(current => (current ? { ...current, bank } : current))} />}
		</Root>
	);
};
